/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import axios from 'axios';

// User roles from PERMISSIONS_MATRIX
export type UserRole =
  | 'admin'
  | 'developer_admin'
  | 'executive'
  | 'regional_director'
  | 'client_director'
  | 'lead_director'
  | 'tutor_director'
  | 'service_director'
  | 'product_director'
  | 'sales_rep'
  | 'support_rep'
  | 'tutor_support'
  | 'course_operations'
  | 'external_user';

export interface User {
  id: number;
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: UserRole[];
  avatarUrl?: string;
  managerId?: number;
  locationId?: number;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const fetchCurrentUser = useCallback(async () => {
    try {
      // Call /v1/users/me endpoint (same as @blueshift-ui/auth)
      // This goes through the API gateway to the Learn Lab users service
      // NOTE: This endpoint is NOT under /vtwa - it's a separate service accessed via API Gateway
      
      // DEBUG: Log environment info
      console.log('[AuthContext Debug] Environment:', {
        DEV: import.meta.env.DEV,
        MODE: import.meta.env.MODE,
        PROD: import.meta.env.PROD,
        VITE_VTWA_API_URL: import.meta.env.VITE_VTWA_API_URL,
        hostname: window.location.hostname,
        origin: window.location.origin,
      });
      
      // In development, use Vite proxy; in production, hit API Gateway directly
      // The /v1/users/me endpoint is on the API Gateway root, NOT under /vtwa
      const apiBaseUrl = import.meta.env.DEV 
        ? '/api/vtwa' 
        : (import.meta.env.VITE_VTWA_API_URL || 'https://api.varsitytutors.com');
      
      const fullUrl = `${apiBaseUrl}/v1/users/me`;
      
      // DEBUG: Log URL construction
      console.log('[AuthContext Debug] URL construction:', {
        apiBaseUrl,
        fullUrl,
        willUseProxy: import.meta.env.DEV,
      });
      
      const response = await axios.get(fullUrl, {
        params: { include_entity: true },
        withCredentials: true, // Send vt_authentication_token cookie
      });
      
      // DEBUG: Log successful response
      console.log('[AuthContext Debug] Response received:', {
        status: response.status,
        hasEntity: !!response.data?.entity,
        hasToken: !!response.data?.token,
      });
      
      const { entity, token } = response.data;
      
      const user: User = {
        id: entity.id,
        uuid: entity.client_uuid || entity.tutor_uuid || `user-${entity.id}`,
        email: entity.email,
        firstName: entity.first_name,
        lastName: entity.last_name,
        fullName: `${entity.first_name} ${entity.last_name}`,
        roles: (token?.roles || []) as UserRole[],
        avatarUrl: entity.avatar_url,
        locationId: entity.jurisdiction_id,
      };

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: unknown) {
      // No valid session cookie - user needs to log in
      const axiosError = error as { 
        response?: { status?: number; data?: unknown }; 
        config?: { url?: string };
        message?: string;
      };
      console.error('[AuthContext Debug] Failed to fetch current user:', {
        message: axiosError.message,
        responseStatus: axiosError.response?.status,
        responseData: axiosError.response?.data,
        requestUrl: axiosError.config?.url,
      });
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null, // Not an error, just not authenticated
      });
    }
  }, []);

  const login = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    
    if (import.meta.env.DEV) {
      // Mock user for development
      const mockUser: User = {
        id: 1,
        uuid: 'dev-user-uuid',
        email: 'dev@varsitytutors.com',
        firstName: 'Dev',
        lastName: 'User',
        fullName: 'Dev User',
        roles: ['admin', 'developer_admin'],
        managerId: undefined,
        locationId: 1,
      };
      
      setState({
        user: mockUser,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } else {
      // Redirect to VT authentication
      // After login, VT will set the vt_authentication_token cookie and redirect back
      const redirectUrl = encodeURIComponent(window.location.href);
      window.location.href = `${import.meta.env.VITE_VTWA_BASE_URL}/login?redirect_to=${redirectUrl}`;
    }
  }, []);

  const logout = useCallback(() => {
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    
    if (!import.meta.env.DEV) {
      // Redirect to VT logout - this will clear the cookie
      window.location.href = `${import.meta.env.VITE_VTWA_BASE_URL}/logout`;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
